/** Kandidaten-Übersicht (Filter) und Tabelle der finalen Kandidatenliste. */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import MoreIcon from '@mui/icons-material/More';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WcIcon from '@mui/icons-material/Wc';
import { Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { fmtNumber } from '../../campaign-wizard.logic';
import { CandidateFilter, WizardCandidate } from '../../campaign-wizard.types';
import { MiSpinner, WizInput, WizLink, WizOutlineButton } from '../../components/wizard-ui';
import { wz } from '../../components/wizard-tokens';
import { UseWizardCandidates } from '../../use-wizard-candidates';
import WizSelect from '../job/wiz-select';
import {
  analysisPauseText,
  distinctValues,
  EMPTY_FILTERS,
  emptyViewOf,
  extraSearchMessage,
  FILTERS,
  forecastText,
  hasTableFilters,
  JUDGE_COLORS,
  JUDGE_LABEL_KEYS,
  JUDGEMENTS,
  pillTitle,
  progressNoticeKey,
  ratingPill,
  loadMoreView,
  remainingProfiles,
  STATUS_FILTERS,
  TableFilters,
} from './candidate-list.logic';

const FILTER_ICONS: Record<CandidateFilter, React.ReactNode> = {
  all: <GroupsIcon sx={{ fontSize: 18 }} />,
  other: <MoreIcon sx={{ fontSize: 18 }} />,
  sex: <WcIcon sx={{ fontSize: 18 }} />,
  blocked: <DoNotDisturbIcon sx={{ fontSize: 18 }} />,
};

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter(x => x !== value) : [...list, value];

interface BulkActions {
  busy: boolean;
  message: string | null;
  rejudge: () => void;
  blacklist: (scope: 'person' | 'company') => void;
  exportCsv: () => void;
}

interface Props {
  candidates: UseWizardCandidates;
  estimate: number | null;
  searchMessage: string | null;
  openId: string | null;
  onOpen: (id: string) => void;
  filters: TableFilters;
  onFilters: (filters: TableFilters) => void;
  /** Zeilen nach den Suchfiltern (Reihenfolge wie angezeigt). */
  visible: WizardCandidate[];
  selected: string[];
  onSelected: (ids: string[]) => void;
  onSelectAll: () => void;
  bulk: BulkActions;
  /** Klick auf „unklares Geschlecht“: Dialog zum Festlegen. */
  onSexClick?: (candidate: WizardCandidate) => void;
  /** „Korrigieren“ an einem übersprungenen eigenen Link: zurück zu Schritt 2. */
  onFixLinks?: () => void;
}

const CandidateTable: React.FC<Props> = ({
  candidates,
  estimate,
  searchMessage,
  openId,
  onOpen,
  filters,
  onFilters,
  visible,
  selected,
  onSelected,
  onSelectAll,
  bulk,
  onSexClick,
  onFixLinks,
}) => {
  const { classes } = useStyles();
  const { t, i18n } = useTranslation();
  const { list, filter } = candidates;
  const remaining = remainingProfiles(estimate ?? list.searchEstimate, list.total);
  const moreView = loadMoreView(list);
  const noticeKey = progressNoticeKey(list);
  // Countdown: der Server rechnet bei jedem Abruf neu, dazwischen tickt die Anzeige lokal.
  const forecast = list.analysisForecast && !list.analysisPause ? list.analysisForecast : null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!forecast) return undefined;
    setNowMs(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [forecast]);
  const forecastLine = forecast ? forecastText(forecast, t, i18n.language, nowMs) : null;
  const progressNotice = noticeKey
    ? {
        warn: noticeKey.warn,
        text:
          noticeKey.key === 'wizListAnalysisPause' && list.analysisPause
            ? analysisPauseText(list.analysisPause, t, i18n.language)
            : forecastLine ?? t(noticeKey.key, { amount: list.analysisPending }),
      }
    : null;
  const emptyView = emptyViewOf(list, visible.length, candidates.startFailure);
  const locations = useMemo(() => distinctValues(list.items, 'location'), [list.items]);
  const companies = useMemo(() => distinctValues(list.items, 'company'), [list.items]);
  const allSelected = visible.length > 0 && visible.every(c => selected.includes(c.id));
  const filtered = hasTableFilters(filters);
  const loadedCount = list.items.length;

  return (
    <div className={classes.card}>
      <div className={classes.side}>
        <div className={classes.sideTitle}>{t('wizListOverview')}</div>
        {FILTERS.map(f => {
          const isSelected = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              className={classes.filter}
              style={{
                background: isSelected ? wz.page : 'transparent',
                color: isSelected ? wz.primary : wz.muted,
              }}
              onClick={() => candidates.setFilter(f.key)}
            >
              {FILTER_ICONS[f.key]}
              <div
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: isSelected ? 700 : 400,
                  textAlign: 'left',
                }}
              >
                {t(f.labelKey)}
              </div>
              <div style={{ fontSize: 12 }}>{list.counts[f.key]}</div>
            </button>
          );
        })}
      </div>

      <div className={classes.main}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {t('wizListHeadline', { active: list.activeCount, total: list.total })}
          </div>
          {remaining !== null ? (
            <div style={{ fontSize: 14, color: wz.muted }}>
              {t('wizListProgress', { amount: fmtNumber(remaining) })}
            </div>
          ) : null}
        </div>

        {list.sources && list.sources.length > 0 ? (
          <div className={classes.sources} data-testid="wiz-link-sources">
            <div className={classes.sourcesTitle}>{t('wizListSources')}</div>
            {list.sources.map(source => {
              const skipped = source.status === 'skipped' || source.status === 'failed';
              return (
                <div
                  key={source.index}
                  className={classes.source}
                  style={skipped ? { borderColor: '#F5D0D0', background: '#FFF7F7' } : undefined}
                  title={source.url}
                >
                  {skipped ? (
                    <ErrorOutlineIcon sx={{ fontSize: 16, color: wz.redFg }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 16, color: wz.success }} />
                  )}
                  <div className={classes.cell} style={{ flex: 1, color: skipped ? wz.redFg : wz.text }}>
                    {t('wizListSourceName', { n: source.index, kind: source.kind })}
                    {skipped ? ` · ${t(source.status === 'failed' ? 'wizListSourceFailed' : 'wizListSourceSkipped')}` : ''}
                  </div>
                  {skipped ? (
                    <button type="button" className={classes.sourceFix} onClick={() => onFixLinks?.()}>
                      {t('wizListSourceFix')}
                    </button>
                  ) : (
                    <div style={{ fontSize: 13, color: wz.text555 }}>
                      {source.total !== null
                        ? t('wizListSourceLoaded', { loaded: source.loaded, total: fmtNumber(source.total) })
                        : t('wizListSourceLoadedOnly', { loaded: source.loaded })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        <div className={classes.filterBar}>
          <div className={classes.searchRow}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <WizInput
                small
                value={filters.text}
                onChange={text => onFilters({ ...filters, text })}
                placeholder={t('wizListSearchPlaceholder')}
                aria-label={t('wizListSearchPlaceholder')}
              />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <WizSelect
                value={filters.location}
                options={[
                  { value: '', label: t('wizListFilterLocationAll') },
                  ...locations.map(value => ({ value, label: value })),
                ]}
                placeholder={t('wizListFilterLocationAll')}
                onChange={location => onFilters({ ...filters, location })}
                ariaLabel={t('wizListColLocation')}
              />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <WizSelect
                value={filters.company}
                options={[
                  { value: '', label: t('wizListFilterCompanyAll') },
                  ...companies.map(value => ({ value, label: value })),
                ]}
                placeholder={t('wizListFilterCompanyAll')}
                onChange={company => onFilters({ ...filters, company })}
                ariaLabel={t('wizListColCompany')}
              />
            </div>
          </div>

          <div className={classes.judgeRow}>
            <div style={{ fontSize: 13, color: wz.muted }}>{t('wizListFilterClassLabel')}</div>
            {JUDGEMENTS.map(j => {
              const on = filters.classes.includes(j);
              return (
                <button
                  key={j}
                  type="button"
                  className={classes.judgeFilter}
                  aria-pressed={on}
                  style={{
                    border: on ? `1px solid ${wz.primary}` : `1px solid ${wz.border}`,
                    background: on ? wz.primarySoft : '#FFF',
                  }}
                  onClick={() => onFilters({ ...filters, classes: toggle(filters.classes, j) })}
                >
                  <span className={classes.dot} style={{ background: JUDGE_COLORS[j] }} />
                  {t(JUDGE_LABEL_KEYS[j])}
                </button>
              );
            })}
          </div>

          <div className={classes.judgeRow}>
            <div style={{ fontSize: 13, color: wz.muted }}>{t('wizListFilterStatusLabel')}</div>
            {STATUS_FILTERS.map(s => {
              const on = filters.statuses.includes(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  className={classes.judgeFilter}
                  aria-pressed={on}
                  style={{
                    border: on ? `1px solid ${wz.primary}` : `1px solid ${wz.border}`,
                    background: on ? wz.primarySoft : '#FFF',
                  }}
                  onClick={() =>
                    onFilters({ ...filters, statuses: toggle(filters.statuses, s.key) })
                  }
                >
                  {t(s.labelKey)}
                </button>
              );
            })}
          </div>

          <div className={classes.countRow}>
            <span data-testid="candidate-count">
              {t('wizListFilterCount', { shown: visible.length, total: loadedCount })}
            </span>
            {selected.length === 0 && visible.length > 0 ? (
              <WizLink onClick={bulk.exportCsv}>{t('wizListBulkExport')}</WizLink>
            ) : null}
            {filtered ? (
              <WizLink onClick={() => onFilters(EMPTY_FILTERS)}>{t('wizListFilterReset')}</WizLink>
            ) : null}
          </div>
        </div>

        {selected.length > 0 ? (
          <div className={classes.bulkBar} role="toolbar">
            <span style={{ fontWeight: 700 }}>
              {t('wizListSelected', { count: selected.length })}
            </span>
            <WizOutlineButton onClick={bulk.rejudge} disabled={bulk.busy}>
              {t('wizListBulkRejudge')}
            </WizOutlineButton>
            <WizOutlineButton onClick={() => bulk.blacklist('person')} disabled={bulk.busy}>
              {t('wizListBulkBlacklistPeople')}
            </WizOutlineButton>
            <WizOutlineButton onClick={() => bulk.blacklist('company')} disabled={bulk.busy}>
              {t('wizListBulkBlacklistCompanies')}
            </WizOutlineButton>
            <WizOutlineButton onClick={bulk.exportCsv} disabled={bulk.busy}>
              {t('wizListBulkExport')}
            </WizOutlineButton>
            {bulk.busy ? <MiSpinner size={16} /> : null}
          </div>
        ) : null}
        {bulk.message ? <div className={classes.bulkMessage}>{bulk.message}</div> : null}

        {candidates.actionError ? (
          <div className={classes.error}>
            {t('wizListActionError')} {candidates.actionError}
          </div>
        ) : null}

        <div className={classes.table}>
          <div className={classes.thead}>
            <input
              type="checkbox"
              aria-label={t('wizListSelectAll')}
              title={t('wizListSelectAll')}
              checked={allSelected}
              disabled={visible.length === 0}
              onChange={onSelectAll}
              style={{ width: 16, margin: 0 }}
            />
            <div style={{ width: 36 }}>{t('wizListColActive')}</div>
            <div style={{ flex: 1.3, minWidth: 0 }}>{t('wizListColName')}</div>
            <div style={{ width: 130 }}>{t('wizListColJudge')}</div>
            <div style={{ flex: 1, minWidth: 0 }}>{t('wizListColCompany')}</div>
            <div style={{ flex: 1.2, minWidth: 0 }}>{t('wizListColTitle')}</div>
            <div style={{ width: 80 }}>{t('wizListColLocation')}</div>
            {list.sources?.length ? <div style={{ width: 44 }}>{t('wizListColSource')}</div> : null}
            <div style={{ width: 28 }} />
          </div>

          {visible.map(item => {
            const opacity = item.active ? 1 : 0.55;
            return (
              <div
                key={item.id}
                className={classes.row}
                style={{ background: openId === item.id ? '#F8F8FF' : '#FFF' }}
              >
                <input
                  type="checkbox"
                  aria-label={t('wizListSelectRow')}
                  checked={selected.includes(item.id)}
                  onChange={() => onSelected(toggle(selected, item.id))}
                  style={{ width: 16, margin: 0 }}
                />
                <button
                  type="button"
                  role="switch"
                  aria-checked={item.active}
                  aria-label={t('wizListToggleActive')}
                  className={classes.track}
                  style={{ background: item.active ? wz.primary : wz.dashed }}
                  onClick={() => void candidates.setActive(item, !item.active)}
                >
                  <span className={classes.knob} style={{ left: item.active ? 18 : 2 }} />
                </button>
                <div
                  style={{
                    flex: 1.3,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity,
                  }}
                >
                  <button type="button" className={classes.name} onClick={() => onOpen(item.id)}>
                    {item.name}
                  </button>
                  {item.inOtherCampaign ? (
                    <Tooltip title={t('wizListOtherCampaign')}>
                      <MoreIcon
                        sx={{ fontSize: 16, color: '#ED6C02', transform: 'rotate(180deg)' }}
                      />
                    </Tooltip>
                  ) : null}
                  {item.unclearSex ? (
                    <Tooltip title={onSexClick ? t('wizListUnclearSexSet') : t('wizListUnclearSex')}>
                      <button
                        type="button"
                        className={classes.iconButton}
                        aria-label={t('wizListUnclearSexSet')}
                        data-testid="wiz-sex-icon"
                        onClick={() => onSexClick?.(item)}
                        disabled={!onSexClick}
                      >
                        <WcIcon sx={{ fontSize: 16, color: '#ED6C02' }} />
                      </button>
                    </Tooltip>
                  ) : null}
                  {item.blocked ? (
                    <Tooltip title={t('wizListBlacklist')}>
                      <DoNotDisturbIcon sx={{ fontSize: 16, color: '#F60404' }} />
                    </Tooltip>
                  ) : null}
                </div>
                <div style={{ width: 130, opacity }}>
                  <span
                    className={classes.pill}
                    style={{
                      background: ratingPill(item).background,
                      color: ratingPill(item).color,
                    }}
                    title={pillTitle(item, t)}
                  >
                    {t(ratingPill(item).labelKey)}
                    {item.manual ? ' ✎' : ''}
                  </span>
                </div>
                <div className={classes.cell} style={{ flex: 1, opacity }}>
                  {item.company}
                </div>
                <div className={classes.cell} style={{ flex: 1.2, color: wz.text555, opacity }}>
                  {item.title}
                </div>
                <div style={{ width: 80, color: wz.text555, opacity }}>{item.location}</div>
                {list.sources?.length ? (
                  <div style={{ width: 44, color: wz.text555, opacity, fontSize: 12 }}>
                    {item.source ? t('wizListSourceShort', { n: item.source }) : ''}
                  </div>
                ) : null}
                <button
                  type="button"
                  className={classes.eye}
                  onClick={() => onOpen(item.id)}
                  aria-label={t('wizListOpen')}
                >
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            );
          })}

          {candidates.loading && list.items.length === 0 ? (
            <div className={classes.notice}>
              <MiSpinner size={18} />
              {t('wizListLoading')}
            </div>
          ) : null}
          {candidates.error ? (
            <div className={classes.notice} style={{ color: wz.redFg }}>
              {t('wizListLoadError')} {candidates.error}
            </div>
          ) : null}
          {!candidates.loading && !candidates.error && progressNotice ? (
            <div
              className={classes.notice}
              role={list.analysisPause ? 'alert' : undefined}
              style={
                list.analysisPause
                  ? { background: wz.warnBg, textAlign: 'left', justifyContent: 'flex-start' }
                  : undefined
              }
            >
              {list.importing ? <MiSpinner size={18} /> : null}
              <span style={progressNotice.warn ? { color: wz.warnFg } : undefined}>
                {progressNotice.text}
              </span>
            </div>
          ) : null}
          {searchMessage && list.total > 0 ? (
            <div className={classes.notice} style={{ color: wz.warnFg }}>
              {searchMessage}
            </div>
          ) : null}
          {!candidates.loading && !candidates.error && moreView ? (
            <div className={classes.notice} style={{ flexDirection: 'column', gap: 10 }} data-testid="wiz-load-more">
              <div>
                {t('wizListMoreAfterStart')}
                {moreView.more > 0 ? ` ${t('wizListMoreAvailable', { count: moreView.more })}` : ''}
              </div>
              {moreView.more > 0 ? (
                <WizOutlineButton
                  onClick={() => void candidates.loadMore()}
                  disabled={candidates.loadingMore}
                  icon={candidates.loadingMore ? <MiSpinner size={16} /> : undefined}
                >
                  {candidates.loadingMore ? t('wizListLoadMoreBusy') : t('wizListLoadMore')}
                </WizOutlineButton>
              ) : null}
            </div>
          ) : null}
          {!candidates.loading &&
          !candidates.error &&
          emptyView === 'loading' &&
          !progressNotice ? (
            <div className={classes.notice}>
              <MiSpinner size={18} />
              {t('wizListImporting')}
            </div>
          ) : null}
          {!candidates.loading &&
          !candidates.error &&
          (emptyView === 'failed' || emptyView === 'notStarted') ? (
            <div className={classes.notice} style={{ flexDirection: 'column' }}>
              <div style={emptyView === 'failed' ? { color: wz.redFg } : undefined}>
                {emptyView === 'failed'
                  ? candidates.startFailure
                    ? t('wizListSearchFailedReason', { reason: candidates.startFailure })
                    : t('wizListSearchFailed')
                  : t('wizListNotStarted')}
              </div>
              <WizOutlineButton
                onClick={() => void candidates.restartSearch()}
                disabled={candidates.restarting}
              >
                {candidates.restarting
                  ? t('wizListRestartBusy')
                  : t(emptyView === 'failed' ? 'wizListRestartSearch' : 'wizListStartSearch')}
              </WizOutlineButton>
              {candidates.restartError ? (
                <div style={{ color: wz.redFg }}>
                  {t('wizListRestartError')} {candidates.restartError}
                </div>
              ) : null}
              {extraSearchMessage(searchMessage, candidates.startFailure) ? (
                <div style={{ color: wz.warnFg }}>{extraSearchMessage(searchMessage, candidates.startFailure)}</div>
              ) : null}
            </div>
          ) : null}
          {!candidates.loading &&
          !candidates.error &&
          (emptyView === 'finished' || emptyView === 'none') ? (
            <div className={classes.notice} style={{ flexDirection: 'column' }}>
              <div>{t('wizListNoCandidates')}</div>
              {searchMessage ? <div style={{ color: wz.warnFg }}>{searchMessage}</div> : null}
            </div>
          ) : null}
          {!candidates.loading && !candidates.error && emptyView === 'filtered' ? (
            <div className={classes.empty}>{t('wizListEmpty')}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()({
  iconButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: 2,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    cursor: 'pointer',
    '&:hover:not(:disabled)': { background: 'rgba(237,108,2,0.12)' },
    '&:disabled': { cursor: 'default' },
  },
  card: {
    background: '#FFF',
    borderRadius: 12,
    boxShadow: wz.shadowCard,
    display: 'flex',
    overflow: 'hidden',
  },
  // Design: width 230 (content-box) + padding 2×12 + border-right 1 = 255 px Außenbreite.
  side: {
    width: 255,
    boxSizing: 'border-box',
    flexShrink: 0,
    borderRight: `1px solid ${wz.border}`,
    padding: '20px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  sideTitle: { fontSize: 13, color: wz.muted, padding: '0 10px 8px' },
  filter: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 5,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
    '&:hover': { background: wz.page },
  },
  main: {
    flex: 1,
    minWidth: 0,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  filterBar: { display: 'flex', flexDirection: 'column', gap: 10 },
  searchRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  countRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    fontSize: 13,
    color: wz.muted,
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    fontSize: 13,
    background: wz.primarySoft,
    borderRadius: 8,
    padding: '8px 12px',
  },
  bulkMessage: { fontSize: 13, color: wz.muted },
  judgeRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  judgeFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    padding: '4px 10px',
    borderRadius: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: wz.text,
  },
  dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  error: {
    fontSize: 13,
    color: wz.redFg,
    background: wz.redBg,
    borderRadius: 8,
    padding: '8px 10px',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${wz.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  thead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    background: wz.page,
    fontSize: 12,
    fontWeight: 500,
    color: wz.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    borderTop: `1px solid ${wz.border}`,
    fontSize: 13,
  },
  track: {
    width: 36,
    height: 20,
    borderRadius: 10,
    position: 'relative',
    flexShrink: 0,
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    transition: 'background 150ms',
  },
  knob: {
    position: 'absolute',
    top: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#FFF',
    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
    transition: 'left 150ms',
  },
  name: {
    fontWeight: 700,
    color: wz.primary,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontSize: 13,
    fontFamily: 'inherit',
  },
  pill: {
    borderRadius: 10,
    padding: '2px 8px',
    fontSize: 12,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    display: 'inline-block',
    boxSizing: 'border-box',
  },
  cell: { minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sources: { display: 'flex', flexDirection: 'column', gap: 8 },
  sourcesTitle: { fontSize: 13, fontWeight: 600, color: wz.textDim },
  source: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    border: `1px solid ${wz.border}`,
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13,
  },
  sourceFix: {
    border: 'none',
    background: 'transparent',
    color: wz.primary,
    fontWeight: 600,
    fontSize: 13,
    fontFamily: 'inherit',
    cursor: 'pointer',
    padding: '8px 4px',
  },
  eye: {
    width: 28,
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    color: wz.primary,
    background: 'transparent',
    border: 'none',
    padding: 0,
  },
  notice: {
    padding: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    fontSize: 14,
    color: wz.muted,
    borderTop: `1px solid ${wz.border}`,
    textAlign: 'center',
  },
  empty: {
    padding: 24,
    textAlign: 'center',
    fontSize: 14,
    color: wz.muted,
    borderTop: `1px solid ${wz.border}`,
  },
});

export default CandidateTable;
